import { Link } from "react-router-dom";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { getRouteProjects } from "@/zapi-frontend/shared/const/router";

export function CookieNoticePage({ className = "" }: { className?: string }) {
  return (
    <article className={"mx-auto max-w-3xl px-4 pt-32 pb-10 text-gray-900 " + className}>
      <header
        className={
          "fixed top-0 left-0 w-full flex items-center border-b-[1px] border-innoveraGrayscale-200 gap-4 px-[17px] py-[0px] h-[72px] font-medium bg-white"
        }
      >
        <Link
          data-testid="header-logo-link"
          to={getRouteProjects()}
          className="w-10 h-10 inline-flex justify-center items-center [&_svg>path]:hover:fill-innoveraOrange-400 [&_svg>path]:transition-[fill] [&_svg>path]:duration-[3ms]"
        >
          <InnoveraIcon className="w-6 h-6" />
        </Link>
        <FigmaTypography component="h1" variant="memo-heading-2">
          Cookie Notice
        </FigmaTypography>
      </header>

      {/* Header */}
      <div className="mb-8 space-y-2">
        <FigmaTypography component="h1" variant="heading-1">
          Cookie Notice
        </FigmaTypography>
        <p className="text-[14px] text-gray-600">
          Last Updated: <time dateTime="2024-11-12">June 2025</time>
        </p>
      </div>

      <section className="mt-8 space-y-4">
        <p className="leading-relaxed">
          This Cookie Notice (“Notice”) explains how Innovera.ai, Inc. (“<strong>Innovera</strong>”, “
          <strong>we</strong>”, “<strong>us</strong>”, and “<strong>our</strong>”) uses cookies, web beacons, pixels,
          tags, local storage, and similar technologies (collectively, “<strong>Tracking Technologies</strong>”) in
          connection with your use of our Services. For purposes of this Notice, the term “Services” refers collectively
          to our marketing website, currently available at{" "}
          <a className="text-blue-500 underline" href="https://www.innovera.ai/">
            https://www.innovera.ai/
          </a>{" "}
          (the “<strong>Site</strong>”); our enterprise innovation management platform, currently available at{" "}
          <a className="text-blue-500 underline" href="https://beta.innovera.ai/">
            https://beta.innovera.ai/
          </a>{" "}
          (the “<strong>Platform</strong>”); as well as all other websites that Innovera operates or manages and which
          link to this Notice.
        </p>
      </section>

      <section className="mt-8" id="what-are-tracking-technologies">
        <FigmaTypography component="h2" variant="memo-heading-1">
          What are Tracking Technologies?
        </FigmaTypography>
        <p className="leading-relaxed">
          Tracking Technologies are widely used by website owners in order to make their websites work, or to work more
          efficiently, as well as to provide reporting information.
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>Web Cookies.</strong> Cookies are small data files that are placed on your computer or mobile device
            when you visit a website. Cookies are then sent back to the originating website on each subsequent visit, or
            to another website that recognizes that cookie, to develop a record of the user’s online activity. Cookies
            set by the website owner (in this case, Innovera) are called "first-party cookies". Cookies set by parties
            other than the website owner are called "third-party cookies". Third-party cookies enable third-party
            features or functionality to be provided on or through the website (e.g., advertising, interactive content,
            and analytics). The parties that set these third-party cookies can recognize your computer both when it
            visits the website in question and also when it visits certain other websites
          </li>
          <li>
            <strong>Pixels, web beacons, and tags.</strong> Pixels, web beacons, and tags are pieces of code or
            transparent graphics embedded in web pages or emails that collect information about your engagement on our
            Services and provide analytical information to the website owner. For example, a pixel tag allows a website
            owner to record that a user has visited a particular web page and web beacons in an email help a website
            owner understand whether messages have been opened, acted on, or forwarded.
          </li>
        </ul>
      </section>

      <section className="mt-8" id="does-innovera-use">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Does Innovera Use Tracking Technologies?
        </FigmaTypography>
        <p className="leading-relaxed">
          Yes. We, as well, as third parties we have partnered with to provide analytics services may use various
          Tracking Technologies to automatically collect information about your use of our Services.
          <br />
          <br />
          In some cases, we may use Tracking Technologies to collect personal information, or information that becomes
          personal information if we combine it with other information. If a combination of information that we collect
          identifies you as an individual, we will treat the combined information as personal information. For more
          information about how we handle personal information we collect from and about you in the course of providing
          our Services, please see our Privacy Notice for Business Partners, Prospective Customers, and Other Business
          Contacts; Privacy Notice for Professional Services Customers; and Privacy Notice for Website and Platform
          Users, as applicable.
        </p>
      </section>

      <section className="mt-8" id="how-innovera-uses">
        <FigmaTypography component="h2" variant="memo-heading-1">
          How Does Innovera Use the Information Gathered Through Tracking Technologies?
        </FigmaTypography>
        <p className="leading-relaxed">
          Innovera uses the information gathered through Tracking Technologies for a variety of purposes including the
          following:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-4">
          <li>
            <strong>Session Usage.</strong> We use user session data, to remember whether you are logged in, hold your
            preferences, ensure that the Services are working properly, and generally track and analyze usage,
            navigational, and other statistical information.
          </li>
          <li>
            <strong>Performance.</strong> We gather analytics information through Tracking Technologies in order to
            track and analyze usage, navigational, and other statistical information. For instance, we may track how
            many users visit a particular page, measure the performance of our off-site advertisement or email
            campaigns, or analyze the popularity of particular content or features. This information helps us improve
            navigation across our Services, identify functionality issues, and informs development of future products
            and features.
          </li>
          <li>
            <strong>Security.</strong> Tracking Technologies also help us ensure the safety and security of your
            websites and online platforms and those who engage with this. This includes detecting, preventing, and
            responding to security incidents and other fraudulent or illegal behavior.
          </li>
        </ul>
      </section>

      <section className="mt-8" id="what-tracking-technologies">
        <FigmaTypography component="h2" variant="memo-heading-1">
          What Tracking Technologies Does Innovera Use?
        </FigmaTypography>

        <p className="leading-relaxed">
          The Tracking Technologies we use can be divided into the general categories described below. However, some of
          Tracking Technologies listed below will only be used if you use certain products, features, or if you select
          certain preferences. Other Tracking Technologies will always be used.
        </p>

        <h3 className="text-lg font-semibold mt-4">Essential</h3>
        <p className="leading-relaxed">
          These Technologies are essential for our Services to function and they cannot be switched off or disabled.
          They allow you to access our Services, applications and tools and help Innovera identify irregular website
          behavior, prevent fraudulent activity, and improve security.
        </p>
        <div className="overflow-x-auto mt-2">
          <table className="table-auto border text-[14px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1">Cookie Name</th>
                <th className="border px-2 py-1">Source</th>
                <th className="border px-2 py-1">Expiry</th>
                <th className="border px-2 py-1">Purpose</th>
                <th className="border px-2 py-1">Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-2 py-1">sessionId</td>
                <td className="border px-2 py-1">Us</td>
                <td className="border px-2 py-1">7 days</td>
                <td className="border px-2 py-1">This cookie allows the user to log in.</td>
                <td className="border px-2 py-1">Platform</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">ssr-caching</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">Less than 1 minute</td>
                <td className="border px-2 py-1">
                  The ssr-caching cookie is set by Wix and indicates how a site was rendered
                </td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">XSRF-TOKEN</td>
                <td className="border px-2 py-1">Us</td>
                <td className="border px-2 py-1">When the session ends (i.e., when the browser is closed)</td>
                <td className="border px-2 py-1">
                  This cookie enhances visitor security by preventing cross- site request forgery.
                </td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">hs</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">When the session ends (i.e., when the browser is closed)</td>
                <td className="border px-2 py-1">Wix platform sets this cookie for security purposes.</td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">svSession</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">1 year 1 month 4 day</td>
                <td className="border px-2 py-1">
                  Wix platform sets this cookie to identify unique visitors and track a visitor's session on a site.
                </td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">bSession</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">1 hour</td>
                <td className="border px-2 py-1">
                  Wix platform sets this cookie in context with load balancing to improve user experience.
                </td>
                <td className="border px-2 py-1">Site</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold mt-6">Functional</h3>
        <p className="leading-relaxed">
          These Tracking Technologies allow us to offer you enhanced functionality and make our website easier to use
          (e.g., by remembering your log-in details, accelerating display of our Site through load balancing, and
          enabling you to communicate with us via live chat).
        </p>
        <div className="overflow-x-auto mt-2">
          <table className="table-auto border text-[14px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1">Cookie Name</th>
                <th className="border px-2 py-1">Source</th>
                <th className="border px-2 py-1">Expiry</th>
                <th className="border px-2 py-1">Purpose</th>
                <th className="border px-2 py-1">Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-2 py-1">acaAffinity</td>
                <td className="border px-2 py-1">Us (automatically through our Azure configs)</td>
                <td className="border px-2 py-1">When the session ends (i.e., when the browser is closed)</td>
                <td className="border px-2 py-1">
                  We use it so that all user requests go to the same server to allow more advanced caching and faster
                  response time.
                </td>
                <td className="border px-2 py-1">Platform</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold mt-6">Analytics</h3>
        <p className="leading-relaxed">
          These Technologies allow us to assess the performance of our Services, including as part of our analytic
          practices to help us understand how individuals use our Services and help us tailor our Services (for example,
          by placing the most sought-after pages where they can be most easily found).
        </p>
        <div className="overflow-x-auto mt-2">
          <table className="table-auto border text-[14px]">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1">Cookie Name</th>
                <th className="border px-2 py-1">Source</th>
                <th className="border px-2 py-1">Expiry</th>
                <th className="border px-2 py-1">Purpose</th>
                <th className="border px-2 py-1">Location</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-2 py-1">_wixCIDX</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">3 months</td>
                <td className="border px-2 py-1">Analytics</td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">_wixUIDX</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">3 months</td>
                <td className="border px-2 py-1">Analytics</td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">_wix_browser_sess</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">When the session ends (i.e., when the browser is closed)</td>
                <td className="border px-2 py-1">Analytics</td>
                <td className="border px-2 py-1">Site</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">consent-policy</td>
                <td className="border px-2 py-1">Wix</td>
                <td className="border px-2 py-1">1 months</td>
                <td className="border px-2 py-1">Analytics</td>
                <td className="border px-2 py-1">Site</td>
              </tr>

              <tr>
                <td className="border px-2 py-1">ph_phc_HZK ymg87yXPUh RxppuQCZRP PPo0B2Pbepb TRZaThe82_ posthog</td>
                <td className="border px-2 py-1">Posthog</td>
                <td className="border px-2 py-1">1 year</td>
                <td className="border px-2 py-1">
                  Analytics (We use Posthog, an analytics platform that collects data on how users use our Platform. It
                  records things like which buttons they click, which pages they see, and how much time they spend on
                  the platform. It doesn’t capture the data users type.)
                </td>
                <td className="border px-2 py-1">Platform</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8" id="control-tracking">
        <FigmaTypography component="h2" variant="memo-heading-1">
          Can I Control Which Tracking Technologies Are Placed When I Use Innovera's Services?
        </FigmaTypography>
        <p className="leading-relaxed">
          Many jurisdictions require or recommend that businesses inform users/visitors about what types of Tracking
          Technologies they use, and in certain circumstances, obtain their consent before placing certain types of
          Tracking Technologies. Innovera displays a “cookie banner” to visitors the first time they land on our Site,
          which links to this Notice and allows you choose whether to accept or reject optional Tracking Technologies.
          <br />
          <br />
          Please note that you cannot reject essential cookies as these are necessary to provide you with the Services.
          You can set your browser to block or alert you about these cookies, but some parts of the Site will not then
          work.
          <br />
          <br />
          In addition, you can configure most browsers to notify you when you receive a new cookie, to reject new
          cookies, and to disable existing cookies. You can learn how to do this by following the instructions here:
        </p>
        <ul className="list-disc pl-6 space-y-1 mt-2">
          <li>
            <a className="text-blue-500 underline" href="https://support.google.com/chrome/answer/95647?hl=en">
              Chrome
            </a>
          </li>
          <li>
            <a
              className="text-blue-500 underline"
              href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac"
            >
              Safari
            </a>
          </li>
          <li>
            <a
              className="text-blue-500 underline"
              href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
            >
              Firefox
            </a>
          </li>
          <li>
            <a
              className="text-blue-500 underline"
              href="https://support.microsoft.com/en-us/help/17442/windows-internet-explorer-delete-manage-cookies"
            >
              Explorer
            </a>
          </li>
          <li>
            <a className="text-blue-500 underline" href="https://help.opera.com/en/latest/web-preferences/#cookies">
              Opera
            </a>
          </li>
        </ul>
        <p className="leading-relaxed mt-4">
          Please be aware that if you choose to stop accepting cookies or other Tracking Technologies, you may not be
          able to use all of the features of our Service.
          <br />
          <br />
          You can also withdraw your consent to optional cookies (those that are not essential) at any time by emailing
          us at{" "}
          <a className="text-blue-500 underline" href="mailto:compliance@innovera.ai">
            compliance@innovera.ai
          </a>
          .
          <br />
          <br />
          For more information about third-party cookies and how to opt-out, you can visit the Network Advertising
          Initiatives opt-out page (available at{" "}
          <a className="text-blue-500 underline" href="https://optout.networkadvertising.org/?c=1">
            https://optout.networkadvertising.org/?c=1
          </a>
          ) or the Digital Advertising Alliance's opt-out page (available at{" "}
          <a className="text-blue-500 underline" href="https://optout.aboutads.info/?c=2&lang=EN">
            https://optout.aboutads.info/?c=2&lang=EN
          </a>
          ). If you reside in the EEA, Switzerland, or UK, please visit{" "}
          <a className="text-blue-500 underline" href="https://youronlinechoices.eu/">
            https://youronlinechoices.eu/
          </a>{" "}
          to learn more about how to opt-out.
        </p>
      </section>
    </article>
  );
}
